if (!customElements.get('shipping-rate-calculator')) {
  class ShippingRateCalculator extends HTMLElement {
    constructor() {
      super();
      this.pollDelay = 750;
      this.maxPollAttempts = 16;
      this.abortController = null;
    }

    connectedCallback() {
      this.toggle = this.querySelector('[data-shipping-toggle]');
      this.body = this.querySelector('[data-shipping-body]');
      this.form = this.querySelector('[data-shipping-form]');
      this.country = this.querySelector('[data-shipping-country]');
      this.province = this.querySelector('[data-shipping-province]');
      this.provinceField = this.querySelector('[data-shipping-province-field]');
      this.postal = this.querySelector('[data-shipping-postal]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.status = this.querySelector('[data-shipping-status]');
      this.rates = this.querySelector('[data-shipping-rates]');
      this.rateList = this.querySelector('[data-shipping-rate-list]');
      this.shippingValue = this.closest('.cart-page__panel')?.querySelector('[data-cart-shipping-value]');
      this.totalValue = this.closest('.cart-page__panel')?.querySelector('[data-cart-total-value]');
      this.subtotal = Number(this.dataset.subtotalCents || 0) / 100;
      this.currency = this.dataset.currency || 'USD';

      if (!this.form || !this.country || !this.postal) return;

      this.toggle?.addEventListener('click', this.handleToggle);
      this.form.addEventListener('submit', this.handleSubmit);
      this.country.addEventListener('change', this.handleCountryChange);
      this.rateList?.addEventListener('change', this.handleRateChange);

      this.restoreAddress();
      this.selectDefaultCountry();
      this.renderProvinces(this.savedAddress?.province);

      if (this.savedAddress?.estimated && this.postal.value) {
        this.open();
        this.calculateRates();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.toggle?.removeEventListener('click', this.handleToggle);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.country?.removeEventListener('change', this.handleCountryChange);
      this.rateList?.removeEventListener('change', this.handleRateChange);
    }

    handleToggle = () => {
      if (this.body?.hidden) {
        this.open();
      } else {
        this.close();
      }
    };

    handleSubmit = (event) => {
      event.preventDefault();
      if (!this.form.reportValidity()) return;
      this.calculateRates();
    };

    handleCountryChange = () => {
      this.renderProvinces();
      this.clearRates('Enter your postal code, then select Get rates.');
    };

    handleRateChange = (event) => {
      const input = event.target.closest('input[name="cart_shipping_rate"]');
      if (!input) return;
      this.applyRate(Number(input.dataset.price), input.dataset.currency || this.currency);
    };

    open() {
      if (!this.body) return;
      this.body.hidden = false;
      this.toggle?.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      if (!this.body) return;
      this.body.hidden = true;
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    selectDefaultCountry() {
      if (this.savedAddress?.country) {
        this.country.value = this.savedAddress.country;
      } else if (this.country.dataset.default) {
        const defaultOption = Array.from(this.country.options).find(
          (option) => option.value.toLowerCase() === this.country.dataset.default.toLowerCase()
        );
        if (defaultOption) this.country.value = defaultOption.value;
      }
    }

    renderProvinces(selectedProvince = '') {
      if (!this.province || !this.provinceField) return;

      const selectedCountry = this.country.options[this.country.selectedIndex];
      let provinces = [];

      try {
        provinces = JSON.parse(selectedCountry?.dataset.provinces || '[]');
      } catch (error) {
        provinces = [];
      }

      this.province.replaceChildren();

      provinces.forEach(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        this.province.appendChild(option);
      });

      const hasProvinces = provinces.length > 0;
      this.province.disabled = !hasProvinces;
      this.provinceField.hidden = !hasProvinces;

      if (hasProvinces && selectedProvince) {
        this.province.value = selectedProvince;
      }
    }

    getAddress() {
      return {
        country: this.country.value,
        province: this.province.disabled ? '' : this.province.value,
        zip: this.postal.value.trim(),
      };
    }

    get storageKey() {
      return 'kg-cart-shipping-address';
    }

    restoreAddress() {
      try {
        this.savedAddress = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        this.savedAddress = null;
      }

      if (this.savedAddress?.zip) this.postal.value = this.savedAddress.zip;
    }

    saveAddress(address, estimated = false) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({ ...address, estimated }));
      } catch (error) {
        // Shipping calculation still works when storage is unavailable.
      }
    }

    buildParams(address) {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', address.country);
      params.set('shipping_address[zip]', address.zip);
      if (address.province) params.set('shipping_address[province]', address.province);
      return params;
    }

    get endpointBase() {
      return window.routes?.cart_url || '/cart';
    }

    async calculateRates() {
      const address = this.getAddress();
      const params = this.buildParams(address);

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading(true);
      this.clearRates('Calculating available shipping rates...');
      this.saveAddress(address, true);

      try {
        const prepareResponse = await fetch(
          `${this.endpointBase}/prepare_shipping_rates.json?${params.toString()}`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            signal: this.abortController.signal,
          }
        );

        if (!prepareResponse.ok) throw await this.createResponseError(prepareResponse);

        const shippingRates = await this.pollRates(params, this.abortController.signal);
        this.renderRates(shippingRates);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clearRates(error.message || 'Shipping rates could not be calculated for this destination.');
        this.resetSummary();
      } finally {
        this.setLoading(false);
      }
    }

    async pollRates(params, signal) {
      for (let attempt = 0; attempt < this.maxPollAttempts; attempt += 1) {
        if (attempt > 0) await this.wait(this.pollDelay, signal);

        const response = await fetch(`${this.endpointBase}/async_shipping_rates.json?${params.toString()}`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          signal,
        });

        if (!response.ok) throw await this.createResponseError(response);

        const data = await response.json();
        if (data?.shipping_rates) return data.shipping_rates;
      }

      throw new Error('Shipping rates are taking longer than expected. Please try again.');
    }

    async createResponseError(response) {
      let message = 'Shipping rates could not be calculated for this destination.';

      try {
        const data = await response.json();
        const errors = data?.errors || data;

        if (typeof errors === 'string') {
          message = errors;
        } else if (errors && typeof errors === 'object') {
          message = Object.values(errors).flat().join(' ');
        }
      } catch (error) {
        // Keep the customer-facing fallback for empty or non-JSON responses.
      }

      return new Error(message);
    }

    renderRates(rates) {
      this.rateList.replaceChildren();

      if (!rates.length) {
        this.clearRates('No shipping rates are available for this destination.');
        this.resetSummary();
        return;
      }

      const sortedRates = [...rates].sort((rateA, rateB) => Number(rateA.price) - Number(rateB.price));

      sortedRates.forEach((rate, index) => {
        const id = `CartShippingRate-${this.id || 'default'}-${index}`;
        const row = document.createElement('label');
        row.className = 'cart-shipping-calculator__rate';
        row.htmlFor = id;

        const input = document.createElement('input');
        input.id = id;
        input.type = 'radio';
        input.name = 'cart_shipping_rate';
        input.value = rate.code || rate.name;
        input.dataset.price = rate.price;
        input.dataset.currency = rate.currency || this.currency;
        input.checked = index === 0;

        const name = document.createElement('span');
        name.className = 'cart-shipping-calculator__rate-name';
        name.textContent = rate.presentment_name || rate.name;

        const price = document.createElement('span');
        price.className = 'cart-shipping-calculator__rate-price';
        price.textContent = this.formatMoney(Number(rate.price), rate.currency || this.currency);

        row.append(input, name, price);
        this.rateList.appendChild(row);
      });

      this.rates.hidden = false;
      this.status.textContent =
        sortedRates.length === 1 ? '1 shipping rate available.' : `${sortedRates.length} shipping rates available.`;
      this.applyRate(Number(sortedRates[0].price), sortedRates[0].currency || this.currency);
    }

    applyRate(price, currency) {
      if (this.shippingValue) {
        this.shippingValue.textContent = price === 0 ? 'Complimentary' : this.formatMoney(price, currency);
      }
      if (this.totalValue) {
        this.totalValue.textContent = this.formatMoney(this.subtotal + price, currency);
      }
    }

    resetSummary() {
      if (this.shippingValue) this.shippingValue.textContent = 'Unavailable';
      if (this.totalValue) this.totalValue.textContent = this.formatMoney(this.subtotal, this.currency);
    }

    clearRates(message) {
      if (this.rateList) this.rateList.replaceChildren();
      if (this.rates) this.rates.hidden = true;
      if (this.status) this.status.textContent = message;
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading ? 'Calculating...' : 'Get rates';
      this.setAttribute('aria-busy', String(isLoading));
    }

    formatMoney(amount, currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (error) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }

    wait(delay, signal) {
      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(resolve, delay);
        signal.addEventListener(
          'abort',
          () => {
            window.clearTimeout(timeout);
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true }
        );
      });
    }
  }

  customElements.define('shipping-rate-calculator', ShippingRateCalculator);
}
